/**
 * Pošta API — typed wrappers around `/v1/posta`.
 *
 * Mirrors the OpenAPI shapes (`ZasilkaListResponseSchema`,
 * `ZasilkaDetailResponseSchema`, `CarrierListResponseSchema`) in
 * `api/src/schemas/posta.ts`.
 */

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@/types/api';
import { api } from '@/lib/api';

/** 0 = odeslaná, 1 = přijatá. */
export type ZasilkaTyp = 0 | 1;

export interface Zasilka {
  id_zasilky: number;
  id_firmy: number;
  typ_zasilky: number;
  id_spolecnosti: number;
  datum_zasilky: string;
  popis_zasilky: string;
  adresat: string;
  podaci_cislo: string;
  adresa: string;
  poznamka: string;
}

export interface ZasilkaWithCarrier extends Zasilka {
  /** Carrier (id_spolecnosti) display name from the JOIN. */
  nazev: string | null;
}

export type Carrier = components['schemas']['Carrier'];
type CarrierBody = components['schemas']['CarrierBody'];
type CarrierResponse = components['schemas']['CarrierResponse'];

export interface ZasilkaListFilters {
  typ_zasilky?: string;
  datum_od?: string;
  datum_do?: string;
  limit?: string;
  offset?: string;
  [key: string]: string | undefined;
}

interface ZasilkaListResponse {
  zasilky: ZasilkaWithCarrier[];
  total: number;
}

function buildQuery(filters: Record<string, string | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value != null && value !== '') params.set(key, value);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useZasilkyQuery(filters: ZasilkaListFilters) {
  return useQuery({
    queryKey: ['posta', filters],
    queryFn: ({ signal }) =>
      api.get<ZasilkaListResponse>(`/v1/posta${buildQuery(filters)}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

export function useZasilkyInfiniteQuery(
  filters: Omit<ZasilkaListFilters, 'limit' | 'offset'>,
  options?: { pageSize?: number },
) {
  const pageSize = options?.pageSize ?? 300;
  return useInfiniteQuery({
    queryKey: ['posta', 'infinite', filters, pageSize],
    queryFn: ({ pageParam, signal }) =>
      api.get<ZasilkaListResponse>(
        `/v1/posta${buildQuery({ ...filters, limit: String(pageSize), offset: String(pageParam) })}`,
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.zasilky.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: (previous) => previous,
  });
}

interface ZasilkaDetailResponse {
  zasilka: Zasilka;
}

export function useZasilkaDetailQuery(id: number | null) {
  return useQuery({
    queryKey: ['posta', id],
    enabled: id != null && Number.isFinite(id),
    // Seeds the zásilka edit form — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<ZasilkaDetailResponse>(`/v1/posta/${id}`, { signal }),
  });
}

type CarrierListResponse = components['schemas']['CarrierListResponse'];

export function useCarriersQuery(enabled = true) {
  return useQuery({
    queryKey: ['posta', 'carriers'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<CarrierListResponse>('/v1/posta/carriers', { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

export interface ZasilkaBody {
  typ_zasilky?: string | number;
  id_spolecnosti?: string | number;
  datum_zasilky?: string;
  popis_zasilky?: string;
  adresat?: string;
  podaci_cislo?: string;
  adresa?: string;
  poznamka?: string;
}

interface ZasilkaCreatedResponse {
  id: number;
}

export function useCreateZasilkaMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ZasilkaBody) =>
      api.post<ZasilkaCreatedResponse>('/v1/posta', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posta'] });
    },
  });
}

export function useUpdateZasilkaMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ZasilkaBody) =>
      api.put<{ status: 'ok' }>(`/v1/posta/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['posta'] });
      qc.invalidateQueries({ queryKey: ['posta', id] });
    },
  });
}

/** Refresh both names in shipment lists and the admin history after lookup edits. */
export function useSaveCarrierMutation(id?: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CarrierBody) => id === undefined
      ? api.post<CarrierResponse>('/v1/posta/carriers', body)
      : api.put<CarrierResponse>(`/v1/posta/carriers/${id}`, body),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['posta'] }),
        qc.invalidateQueries({ queryKey: ['historie', 'posta'] }),
      ]);
    },
  });
}

export function useDeleteCarrierMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<components['schemas']['StatusOkResponse']>(`/v1/posta/carriers/${id}`),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['posta'] }),
        qc.invalidateQueries({ queryKey: ['historie', 'posta'] }),
      ]);
    },
  });
}
